package es.cragma.fichajefacil.data

import androidx.compose.runtime.mutableStateListOf
import es.cragma.fichajefacil.getCurrentTimeMillis
import es.cragma.fichajefacil.getDeviceBatteryLevel
import es.cragma.fichajefacil.getDeviceModelName
import es.cragma.fichajefacil.getDeviceIpAddress
import es.cragma.fichajefacil.getDeviceLanguage
import es.cragma.fichajefacil.model.*

class AppRepository {

    private val companies = mutableStateListOf<Company>()
    private val users = mutableStateListOf<User>()
    private val clockInRecords = mutableStateListOf<ClockInRecord>()
    private val auditLogs = mutableStateListOf<SecurityAuditEntry>()
    private var activeSessionEmail: String? = null

    init {
        seedInitialData()
    }

    private fun seedInitialData() {
        // Only demo user seeded in production
        val demoUser = User(
            id = "user-demo",
            firstName = "Usuario",
            lastName = "Demostración",
            email = "demo@cragma.es",
            phone = "600000000",
            docType = IdentityDocumentType.DNI,
            docNumber = "00000000D",
            companyId = null,
            role = UserRole.WORKER,
            position = "Invitado Demo",
            department = "Demostración",
            isDemoUser = true
        )

        users.add(demoUser)

        val globalAdminUser = User(
            id = "user-global-admin",
            firstName = "Administrador",
            lastName = "Global",
            email = "apps@cragma.es",
            phone = "600000000",
            docType = IdentityDocumentType.DNI,
            docNumber = "00000000A",
            companyId = null,
            role = UserRole.GLOBAL_ADMIN,
            position = "Global Administrator",
            department = "Dirección"
        )
        users.add(globalAdminUser)
    }

    // --- Authentication & Session Persistence ---
    fun loginWithCloudSync(
        emailInput: String,
        onResult: (List<User>) -> Unit
    ) {
        val cleanEmail = emailInput.trim().lowercase()

        if (cleanEmail == "apps@cragma.es" || cleanEmail == "admin@cragma.es") {
            val globalAdmin = users.find { it.email.lowercase() == cleanEmail } ?: User(
                id = "user-global-admin",
                firstName = "Administrador",
                lastName = "Global",
                email = "apps@cragma.es",
                phone = "600000000",
                docType = IdentityDocumentType.DNI,
                docNumber = "00000000A",
                companyId = null,
                role = UserRole.GLOBAL_ADMIN,
                position = "Global Administrator",
                department = "Dirección"
            ).also { users.add(it) }
            saveUserSession(globalAdmin)
            onResult(listOf(globalAdmin))
            return
        }

        if (cleanEmail == "demo@cragma.es") {
            val demoUser = users.find { it.email.lowercase() == "demo@cragma.es" } ?: User(
                id = "user-demo",
                firstName = "Usuario",
                lastName = "Demostración",
                email = "demo@cragma.es",
                phone = "600000000",
                docType = IdentityDocumentType.DNI,
                docNumber = "00000000D",
                companyId = null,
                role = UserRole.WORKER,
                position = "Invitado Demo",
                department = "Demostración",
                isDemoUser = true
            ).also { users.add(it) }
            clearDemoSessionData()
            saveUserSession(demoUser)
            onResult(listOf(demoUser))
            return
        }

        FirestoreSyncManager.fetchUserAccountsFromCloud(cleanEmail) { cloudResults, cloudClockIns ->
            cloudResults.forEach { (user, company) ->
                if (company != null) {
                    val compIdx = companies.indexOfFirst { it.id == company.id }
                    if (compIdx == -1) {
                        companies.add(company)
                    } else {
                        companies[compIdx] = company
                    }
                }

                val userIdx = users.indexOfFirst { it.id == user.id || (it.email.lowercase() == user.email.lowercase() && it.companyId == user.companyId) }
                if (userIdx == -1) {
                    users.add(user)
                } else {
                    users[userIdx] = user
                }
            }

            cloudClockIns.forEach { record ->
                val rIdx = clockInRecords.indexOfFirst { it.id == record.id }
                if (rIdx == -1) {
                    clockInRecords.add(record)
                } else {
                    clockInRecords[rIdx] = record
                }
            }

            val matchedUsers = getUsersByEmail(cleanEmail)
            if (matchedUsers.isNotEmpty()) {
                saveUserSession(matchedUsers.first())
            }
            onResult(matchedUsers)
        }
    }

    fun login(emailInput: String): User? {
        val cleanEmail = emailInput.trim().lowercase()
        var user = users.find { it.email.lowercase() == cleanEmail }

        if (user == null && cleanEmail == "apps@cragma.es") {
            user = User(
                id = "user-global-admin",
                firstName = "Administrador",
                lastName = "Global",
                email = "apps@cragma.es",
                phone = "600000000",
                docType = IdentityDocumentType.DNI,
                docNumber = "00000000A",
                companyId = null,
                role = UserRole.GLOBAL_ADMIN,
                position = "Global Administrator",
                department = "Dirección"
            )
            users.add(user)
            FirestoreSyncManager.syncEmployeeByEmailToCloud(user)
        }

        if (user != null) {
            saveUserSession(user)
            if (user.isDemoUser) {
                clearDemoSessionData() // Always start demo session fresh!
            }
        }
        return user
    }

    fun saveUserSession(user: User) {
        val clean = user.email.trim().lowercase()
        activeSessionEmail = clean
        try {
            platformSaveSessionEmail(clean)
        } catch (_: Throwable) {}
    }

    fun getSavedSessionEmail(): String? {
        return try {
            val email = platformGetSessionEmail() ?: activeSessionEmail
            if (email.isNullOrBlank()) null else email.trim().lowercase()
        } catch (_: Throwable) {
            activeSessionEmail?.trim()?.lowercase()
        }
    }

    fun getSavedSessionUser(): User? {
        val email = getSavedSessionEmail() ?: return null
        return users.find { it.email.trim().lowercase() == email }
    }

    fun getUsersByEmail(email: String): List<User> {
        val cleanEmail = email.trim().lowercase()
        return users.filter { it.email.lowercase() == cleanEmail && it.isActive }
    }

    fun clearUserSession() {
        if (activeSessionEmail == "demo@cragma.es") {
            clearDemoSessionData()
        }
        activeSessionEmail = null
        try {
            platformClearSessionEmail()
        } catch (_: Throwable) {}
    }

    fun clearDemoSessionData() {
        clockInRecords.removeAll { it.userId == "user-demo" }
    }

    // --- Unique 10-Character Company ID Generator ---
    fun generateUniqueCompanyId(): String {
        val chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
        while (true) {
            val candidate = (1..10).map { chars.random() }.joinToString("")
            if (companies.none { it.id == candidate }) {
                return candidate
            }
        }
    }

    // --- Companies ---
    fun getAllCompanies(): List<Company> = companies

    fun getCompany(id: String): Company? = companies.find { it.id == id }

    fun registerCompany(
        name: String,
        cif: String,
        address: String,
        city: String,
        country: String,
        plan: CompanyPlan,
        adminName: String,
        adminPhone: String,
        adminEmail: String,
        adminPassword: String
    ): Pair<Company, User> {
        val newCompId = generateUniqueCompanyId()
        val newCompany = Company(
            id = newCompId,
            name = name,
            cif = cif,
            address = address,
            city = city,
            country = country,
            plan = plan,
            maxEmployees = plan.defaultLimit
        )
        companies.add(newCompany)

        val nameParts = adminName.trim().split(" ", limit = 2)
        val firstName = nameParts.getOrNull(0) ?: adminName
        val lastName = nameParts.getOrNull(1) ?: ""

        val newAdminUser = User(
            id = "user-${users.size + 1}",
            firstName = firstName,
            lastName = lastName,
            email = adminEmail.trim(),
            phone = adminPhone.trim(),
            companyId = newCompId,
            role = UserRole.COMPANY_ADMIN,
            position = "Administrador de Empresa",
            department = "Administración"
        )
        users.add(newAdminUser)

        FirestoreSyncManager.registerCompanyAndAdmin(newCompany, newAdminUser, adminPassword)

        return Pair(newCompany, newAdminUser)
    }

    fun updateCompanyPlan(companyId: String, newPlan: CompanyPlan, customMax: Int) {
        val index = companies.indexOfFirst { it.id == companyId }
        if (index != -1) {
            val old = companies[index]
            val updated = old.copy(plan = newPlan, maxEmployees = customMax)
            companies[index] = updated
            FirestoreSyncManager.syncCompanyToCloud(updated)
        }
    }

    fun updateCompanyNameAndLogo(companyId: String, newName: String, newLogoUrl: String?): Boolean {
        val index = companies.indexOfFirst { it.id == companyId }
        if (index != -1) {
            val old = companies[index]
            val updated = old.copy(
                name = newName.trim().ifBlank { old.name },
                logoUrl = newLogoUrl ?: old.logoUrl
            )
            companies[index] = updated
            FirestoreSyncManager.syncCompanyToCloud(updated)
            return true
        }
        return false
    }

    // --- Company Custom Positions & Departments ---
    fun addPositionToCompany(companyId: String, positionName: String): Boolean {
        val company = getCompany(companyId) ?: return false
        val clean = positionName.trim()
        if (clean.isBlank() || company.positions.any { it.equals(clean, ignoreCase = true) }) return false
        val index = companies.indexOfFirst { it.id == companyId }
        if (index != -1) {
            companies[index] = company.copy(positions = company.positions + clean)
            return true
        }
        return false
    }

    fun addDepartmentToCompany(companyId: String, departmentName: String): Boolean {
        val company = getCompany(companyId) ?: return false
        val clean = departmentName.trim()
        if (clean.isBlank() || company.departments.any { it.equals(clean, ignoreCase = true) }) return false
        val index = companies.indexOfFirst { it.id == companyId }
        if (index != -1) {
            companies[index] = company.copy(departments = company.departments + clean)
            return true
        }
        return false
    }

    fun updateCompanyLocationPolicy(companyId: String, allowUnverifiedByDefault: Boolean) {
        val index = companies.indexOfFirst { it.id == companyId }
        if (index != -1) {
            val old = companies[index]
            companies[index] = old.copy(allowUnverifiedLocationByDefault = allowUnverifiedByDefault)
        }
    }

    fun addWorkplaceToCompany(
        companyId: String,
        name: String,
        address: String,
        latitude: Double,
        longitude: Double,
        radiusMeters: Int
    ): Boolean {
        val company = getCompany(companyId) ?: return false
        val newWorkplace = WorkplaceLocation(
            id = "wp-${company.workplaces.size + 1}",
            name = name.trim().ifBlank { "Centro de Trabajo" },
            address = address.trim().ifBlank { "Dirección Registrada" },
            latitude = latitude,
            longitude = longitude,
            radiusMeters = radiusMeters
        )
        val index = companies.indexOfFirst { it.id == companyId }
        if (index != -1) {
            companies[index] = company.copy(workplaces = company.workplaces + newWorkplace)
            return true
        }
        return false
    }

    // --- Users & Employee Management ---
    fun getUsersByCompany(requesterUser: User, companyId: String): List<User> {
        if (!EncryptionUtils.assertTenantAccess(requesterUser.companyId, companyId, requesterUser.role.name)) {
            return emptyList() // Strict Tenant Isolation Guard
        }

        val companyUsers = users.filter { it.companyId == companyId }

        // Role-based visibility isolation:
        // WORKER role can ONLY see their own profile, not other workers' personal data!
        if (requesterUser.role == UserRole.WORKER) {
            return companyUsers.filter { it.id == requesterUser.id }
        }

        return companyUsers
    }

    fun createWorker(
        creatorUser: User,
        companyId: String,
        firstName: String,
        lastName: String,
        email: String,
        phone: String,
        docType: IdentityDocumentType,
        docNumber: String,
        position: String,
        department: String,
        assignedRole: UserRole,
        allowUnverifiedLocation: Boolean
    ): Result<User> {
        val company = getCompany(companyId)
            ?: return Result.failure(Exception("Empresa no encontrada"))

        // 1. Employee Limit Validation
        val currentEmployees = users.filter { it.companyId == companyId && it.isActive }.size
        if (currentEmployees >= company.maxEmployees) {
            return Result.failure(
                Exception("Límite de empleados alcanzado (${company.maxEmployees} máx. según el plan ${company.plan.displayName}). Modifica el plan para ampliar el cupo.")
            )
        }

        // 2. Role Hierarchy Validation
        if (creatorUser.role == UserRole.HR) {
            if (assignedRole == UserRole.COMPANY_ADMIN || assignedRole == UserRole.GLOBAL_ADMIN) {
                return Result.failure(
                    Exception("Permiso denegado: Un usuario de Recursos Humanos no puede asignar el rol de Administrador de Empresa.")
                )
            }
        }

        // 3. Multi-Company Employee Registration:
        // Search if employee record already exists within THIS specific company
        val existingIndexInCompany = users.indexOfFirst {
            it.email.equals(email.trim(), ignoreCase = true) && it.companyId == companyId
        }

        if (existingIndexInCompany != -1) {
            val existingUser = users[existingIndexInCompany]
            val updatedUser = existingUser.copy(
                firstName = firstName.trim().ifBlank { existingUser.firstName },
                lastName = lastName.trim().ifBlank { existingUser.lastName },
                phone = phone.trim().ifBlank { existingUser.phone },
                docType = docType,
                docNumber = docNumber.trim().ifBlank { existingUser.docNumber },
                companyId = companyId,
                role = assignedRole,
                position = position.trim().ifBlank { "Empleado" },
                department = department.trim().ifBlank { "General" },
                isActive = true,
                allowUnverifiedLocationClockIn = allowUnverifiedLocation
            )
            users[existingIndexInCompany] = updatedUser
            FirestoreSyncManager.syncEmployeeByEmailToCloud(updatedUser)
            auditLogs.add(
                SecurityAuditEntry(
                    id = "aud-${auditLogs.size + 1}",
                    companyId = companyId,
                    action = "UPDATE_WORKER",
                    performedBy = creatorUser.name,
                    details = "Actualización de empleado en empresa: ${updatedUser.name} (${updatedUser.role.displayName})"
                )
            )
            return Result.success(updatedUser)
        }

        // If employee exists in another company, reuse personal fields if blank and create account for THIS company
        val existingInOtherCompany = users.find { it.email.equals(email.trim(), ignoreCase = true) }

        val newUser = User(
            id = "user-${users.size + 1}",
            firstName = firstName.trim().ifBlank { existingInOtherCompany?.firstName ?: "" },
            lastName = lastName.trim().ifBlank { existingInOtherCompany?.lastName ?: "" },
            email = email.trim(),
            phone = phone.trim().ifBlank { existingInOtherCompany?.phone ?: "600000000" },
            docType = docType,
            docNumber = docNumber.trim().ifBlank { existingInOtherCompany?.docNumber ?: "12345678Z" },
            companyId = companyId,
            role = assignedRole,
            position = position.trim().ifBlank { "Empleado" },
            department = department.trim().ifBlank { "General" },
            isActive = true,
            allowUnverifiedLocationClockIn = allowUnverifiedLocation
        )

        users.add(newUser)
        FirestoreSyncManager.syncEmployeeByEmailToCloud(newUser)
        auditLogs.add(
            SecurityAuditEntry(
                id = "aud-${auditLogs.size + 1}",
                companyId = companyId,
                action = "CREATE_WORKER",
                performedBy = creatorUser.name,
                details = "Alta de empleado: ${newUser.name} (${newUser.role.displayName})"
            )
        )

        return Result.success(newUser)
    }

    fun updateWorkerDetails(
        modifierUser: User,
        targetUserId: String,
        firstName: String,
        lastName: String,
        phone: String,
        docType: IdentityDocumentType,
        docNumber: String,
        position: String,
        department: String,
        assignedRole: UserRole,
        isActive: Boolean,
        allowUnverifiedLocation: Boolean
    ): Result<Unit> {
        val index = users.indexOfFirst { it.id == targetUserId }
        if (index == -1) return Result.failure(Exception("Usuario no encontrado"))

        val targetUser = users[index]

        // Role Hierarchy Guard: HR cannot promote someone to Company Admin
        if (modifierUser.role == UserRole.HR && (assignedRole == UserRole.COMPANY_ADMIN || assignedRole == UserRole.GLOBAL_ADMIN)) {
            return Result.failure(Exception("Permiso denegado: Recursos Humanos no puede asignar roles de Administrador."))
        }

        val updatedUser = targetUser.copy(
            firstName = firstName.trim(),
            lastName = lastName.trim(),
            phone = phone.trim(),
            docType = docType,
            docNumber = docNumber.trim(),
            position = position.trim(),
            department = department.trim(),
            role = assignedRole,
            isActive = isActive,
            allowUnverifiedLocationClockIn = allowUnverifiedLocation
        )
        users[index] = updatedUser
        FirestoreSyncManager.syncEmployeeByEmailToCloud(updatedUser)

        auditLogs.add(
            SecurityAuditEntry(
                id = "aud-${auditLogs.size + 1}",
                companyId = targetUser.companyId ?: "",
                action = if (isActive) "UPDATE_WORKER" else "TERMINATE_WORKER",
                performedBy = modifierUser.name,
                details = "Modificación de empleado: ${targetUser.name} (Estado Activo: $isActive)"
            )
        )

        return Result.success(Unit)
    }

    // --- Clock In Operations ---
    fun getActiveClockIn(userId: String, userEmail: String? = null): ClockInRecord? {
        val cleanId = userId.trim().lowercase()
        val cleanMail = (userEmail ?: userId).trim().lowercase()
        return clockInRecords.find { record ->
            val recUser = record.userId.trim().lowercase()
            (recUser == cleanId || recUser == cleanMail) &&
            (record.endTimeMillis == null || record.endTimeMillis == 0L)
        }
    }

    fun getClockInHistory(requesterUser: User, targetUserId: String): List<ClockInRecord> {
        val cleanTargetId = targetUserId.trim().lowercase()
        val cleanReqEmail = requesterUser.email.trim().lowercase()

        // Isolation check: WORKER can ONLY see their own records!
        if (requesterUser.role == UserRole.WORKER && requesterUser.id.lowercase() != cleanTargetId && cleanReqEmail != cleanTargetId) {
            return emptyList()
        }

        return clockInRecords
            .filter {
                val recUser = it.userId.trim().lowercase()
                recUser == cleanTargetId || recUser == cleanReqEmail
            }
            .sortedByDescending { it.startTimeMillis }
    }

    fun getAllClockInsForCompany(requesterUser: User, companyId: String): List<ClockInRecord> {
        if (!EncryptionUtils.assertTenantAccess(requesterUser.companyId, companyId, requesterUser.role.name)) {
            return emptyList()
        }

        // WORKER role cannot see company-wide clock ins
        if (requesterUser.role == UserRole.WORKER) {
            return clockInRecords.filter { it.userId == requesterUser.id }
        }

        return clockInRecords
            .filter { it.companyId == companyId }
            .sortedByDescending { it.startTimeMillis }
    }

    fun startClockIn(
        user: User,
        isLocationVerified: Boolean,
        location: String?,
        latitude: Double? = null,
        longitude: Double? = null,
        altitude: Double? = null,
        city: String = "",
        country: String = "España",
        isAtWorkplace: Boolean = false,
        workplaceName: String? = null
    ): ClockInRecord {
        val active = getActiveClockIn(user.id)
        if (active != null) return active

        val now = getCurrentTimeMillis()
        val recordId = generateUnique20CharClockInId(now)

        val newRecord = ClockInRecord(
            id = recordId,
            userId = user.email.trim().lowercase(),
            companyId = user.companyId ?: "",
            userName = user.name,
            userRole = user.role,
            startTimeMillis = now,
            startDateString = formatDateTimeString(now),
            isLocationVerified = isLocationVerified,
            startLocation = location,
            startLatitude = latitude,
            startLongitude = longitude,
            startAltitude = altitude,
            startCity = city,
            startCountry = country,
            startIpAddress = getDeviceIpAddress(),
            deviceModel = getDeviceModelName(),
            batteryLevel = getDeviceBatteryLevel(),
            language = getDeviceLanguage(),
            isStartAtWorkplace = isAtWorkplace,
            startWorkplaceName = workplaceName
        )
        clockInRecords.add(newRecord)
        FirestoreSyncManager.syncClockInToCloud(newRecord)
        return newRecord
    }

    fun startBreak(clockInId: String) {
        val index = clockInRecords.indexOfFirst { it.id == clockInId }
        if (index != -1) {
            val record = clockInRecords[index]
            if (record.currentStatus == ClockInStatus.WORKING) {
                val now = getCurrentTimeMillis()
                val breakId = generateUnique20CharClockInId(now)
                val newBreaks = record.breaks.toMutableList().apply {
                    add(
                        BreakRecord(
                            id = breakId,
                            startTimeMillis = now,
                            startDateString = formatDateTimeString(now)
                        )
                    )
                }
                val updated = record.copy(breaks = newBreaks)
                clockInRecords[index] = updated
                FirestoreSyncManager.syncClockInToCloud(updated)
            }
        }
    }

    fun resumeFromBreak(clockInId: String) {
        val index = clockInRecords.indexOfFirst { it.id == clockInId }
        if (index != -1) {
            val record = clockInRecords[index]
            val activeBreakIndex = record.breaks.indexOfFirst { it.endTimeMillis == null }
            if (activeBreakIndex != -1) {
                val now = getCurrentTimeMillis()
                val newBreaks = record.breaks.toMutableList()
                val oldBreak = newBreaks[activeBreakIndex]
                newBreaks[activeBreakIndex] = oldBreak.copy(
                    endTimeMillis = now,
                    endDateString = formatDateTimeString(now)
                )
                val updated = record.copy(breaks = newBreaks)
                clockInRecords[index] = updated
                FirestoreSyncManager.syncClockInToCloud(updated)
            }
        }
    }

    fun endClockIn(
        clockInId: String,
        endLocation: String?,
        latitude: Double? = null,
        longitude: Double? = null,
        altitude: Double? = null,
        isAtWorkplace: Boolean = false,
        workplaceName: String? = null
    ) {
        val index = clockInRecords.indexOfFirst { it.id == clockInId }
        if (index != -1) {
            val record = clockInRecords[index]
            val now = getCurrentTimeMillis()

            // Close any open breaks using 'now' timestamp
            val closedBreaks = record.breaks.map { b ->
                if (b.endTimeMillis == null || b.endTimeMillis == 0L) {
                    b.copy(
                        endTimeMillis = now,
                        endDateString = formatDateTimeString(now)
                    )
                } else b
            }.toMutableList()

            val updated = record.copy(
                endTimeMillis = now,
                endDateString = formatDateTimeString(now),
                breaks = closedBreaks,
                endLocation = endLocation,
                endLatitude = latitude,
                endLongitude = longitude,
                endAltitude = altitude,
                endIpAddress = getDeviceIpAddress(),
                endBatteryLevel = getDeviceBatteryLevel(),
                isEndAtWorkplace = isAtWorkplace,
                endWorkplaceName = workplaceName
            )
            clockInRecords[index] = updated
            FirestoreSyncManager.syncClockInToCloud(updated)
        }
    }

    fun rectifyClockInRecord(
        adminUser: User,
        clockInId: String,
        newStartTimeMillis: Long,
        newEndTimeMillis: Long?,
        reason: String
    ): Result<Unit> {
        val index = clockInRecords.indexOfFirst { it.id == clockInId }
        if (index == -1) return Result.failure(Exception("Registro de fichaje no encontrado."))

        val record = clockInRecords[index]

        if (adminUser.role != UserRole.COMPANY_ADMIN && adminUser.role != UserRole.HR && adminUser.role != UserRole.GLOBAL_ADMIN) {
            return Result.failure(Exception("Permiso denegado: Solo administradores o Recursos Humanos pueden rectificar horas."))
        }

        val updated = record.copy(
            startTimeMillis = newStartTimeMillis,
            endTimeMillis = newEndTimeMillis,
            isRectified = true,
            rectifiedBy = adminUser.name + " (" + adminUser.role.displayName + ")",
            rectifiedAtMillis = getCurrentTimeMillis(),
            rectificationReason = reason.trim().ifBlank { "Rectificación administrativa de jornada" }
        )
        clockInRecords[index] = updated
        FirestoreSyncManager.syncClockInToCloud(updated)

        auditLogs.add(
            SecurityAuditEntry(
                id = "aud-${auditLogs.size + 1}",
                companyId = record.companyId,
                action = "RECTIFY_CLOCK_IN",
                performedBy = adminUser.name,
                details = "Rectificación de fichaje #${record.id} para ${record.userName}: ${updated.rectificationReason}"
            )
        )

        return Result.success(Unit)
    }

    // --- HR / Manager status overview ---
    fun getUserShiftStatusesForCompany(requesterUser: User, companyId: String): List<UserShiftStatus> {
        val companyUsers = getUsersByCompany(requesterUser, companyId)
        val now = getCurrentTimeMillis()

        return companyUsers.map { user ->
            val active = getActiveClockIn(user.id)
            val userHistory = getClockInHistory(requesterUser, user.id)
            val lastFinished = userHistory.firstOrNull { it.endTimeMillis != null }

            val status = active?.currentStatus ?: ClockInStatus.NOT_STARTED

            val lastShiftEndedText = if (active != null) {
                "Jornada en curso"
            } else if (lastFinished != null && lastFinished.endTimeMillis != null) {
                val diffMillis = now - lastFinished.endTimeMillis!!
                val hours = diffMillis / (3600_000)
                val minutes = (diffMillis % (3600_000)) / (60_000)
                if (hours > 24) {
                    val days = hours / 24
                    "Finalizó hace $days día(s)"
                } else if (hours > 0) {
                    "Finalizó hace ${hours}h ${minutes}m"
                } else {
                    "Finalizó hace ${minutes} min"
                }
            } else {
                "Sin fichajes registrados"
            }

            UserShiftStatus(
                user = user,
                status = status,
                activeClockIn = active,
                lastClockIn = lastFinished,
                lastShiftEndedText = lastShiftEndedText
            )
        }
    }
}
